import React, { useEffect, useRef, useState } from 'react';
import { Alert, AlertTitle, Snackbar, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useStoreApprovalStatus } from '../hooks/useStoreApprovalStatus';

export const APPROVED_NOTICE = 'Your store is approved. You now have full access.';
export const REJECTED_NOTICE = 'Your store application was not approved. Contact support to petition this decision.';

type Decision = 'approved' | 'rejected';

/**
 * Explore-mode notice for a store whose application is still under review.
 * Mirrors the server's pendingOrganizationGuard: catalog browsing works,
 * integrations, pricing and sync return 403 until an admin approves.
 *
 * `useStoreApprovalStatus` polls while the store is pending. When the status
 * leaves `pending_verification` this refetches every query (the shell's gating
 * data: subscription, feature flags, organization, integrations) so pages
 * that were refused unlock without a reload, and says so in a toast.
 */
export const PendingApprovalBanner: React.FC = () => {
  const { isPendingReview, isRejected, approval, organizationStatus, organizationId } = useStoreApprovalStatus();
  const queryClient = useQueryClient();
  // The organization that was seen pending; a decision only counts for that org.
  const pendingFor = useRef<{ orgId: string | null } | null>(null);
  const [open, setOpen] = useState(false);
  // Held until the snackbar has finished leaving so its text does not flip mid-exit.
  const [decision, setDecision] = useState<Decision | null>(null);

  useEffect(() => {
    if (pendingFor.current && organizationId && pendingFor.current.orgId && organizationId !== pendingFor.current.orgId) {
      // View-as exit or a default-organization switch: not a decision on the pending org.
      pendingFor.current = null;
    }
    if (isPendingReview) {
      pendingFor.current = { orgId: organizationId };
      return;
    }
    if (!pendingFor.current) return;
    if (organizationStatus === 'active' || isRejected) {
      pendingFor.current = null;
      setDecision(organizationStatus === 'active' ? 'approved' : 'rejected');
      setOpen(true);
      queryClient.invalidateQueries();
    }
  }, [isPendingReview, isRejected, organizationStatus, organizationId, queryClient]);

  const infoRequest = approval?.infoRequest;

  return (
    <>
      {isPendingReview && (
        <Alert severity="info" sx={{ m: 2, mb: 0, flexShrink: 0 }}>
          <AlertTitle>Your application is under review</AlertTitle>
          You can explore the catalog now. Connecting integrations, pricing tools and sync unlock as soon as
          the Motzu team approves your store.
          {infoRequest?.message && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>We need one more thing:</strong> {infoRequest.message}
            </Typography>
          )}
        </Alert>
      )}
      {isRejected && !isPendingReview && (
        <Alert severity="error" sx={{ m: 2, mb: 0, flexShrink: 0 }}>
          <AlertTitle>Your application was not approved</AlertTitle>
          {REJECTED_NOTICE}
        </Alert>
      )}
      <Snackbar
        open={open}
        autoHideDuration={8000}
        onClose={() => setOpen(false)}
        TransitionProps={{ onExited: () => setDecision(null) }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={decision === 'rejected' ? 'error' : 'success'}
          variant="filled"
          onClose={() => setOpen(false)}
        >
          {decision === 'rejected' ? REJECTED_NOTICE : APPROVED_NOTICE}
        </Alert>
      </Snackbar>
    </>
  );
};
